import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface WorkflowEvent {
  id: string;
  timestamp: Date;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  details?: any;
}

interface WorkflowEventLogProps {
  maxEvents?: number;
}

export const WorkflowEventLog: React.FC<WorkflowEventLogProps> = ({ maxEvents = 50 }) => {
  const { darkMode } = useTheme();
  const [events, setEvents] = useState<WorkflowEvent[]>([]);
  const [expanded, setExpanded] = useState(false);
  
  // Subscribe to workflow events
  useEffect(() => {
    const handleWorkflowEvent = (event: CustomEvent) => {
      const { type, message, details } = event.detail;
      
      setEvents(prev => {
        const newEvents = [
          {
            id: Math.random().toString(36).substring(2, 9),
            timestamp: new Date(),
            type,
            message,
            details
          },
          ...prev
        ];
        
        // Limit the number of events
        return newEvents.slice(0, maxEvents);
      });
    };
    
    // Add event listener
    window.addEventListener('workflow-event', handleWorkflowEvent as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('workflow-event', handleWorkflowEvent as EventListener);
    };
  }, [maxEvents]);
  
  // Helper function to dispatch events
  const getEventTypeStyle = (type: string) => {
    switch (type) {
      case 'success':
        return darkMode ? 'text-green-400' : 'text-green-600';
      case 'error':
        return darkMode ? 'text-red-400' : 'text-red-600';
      case 'warning':
        return darkMode ? 'text-yellow-400' : 'text-yellow-600';
      case 'info':
      default:
        return darkMode ? 'text-blue-400' : 'text-blue-600';
    }
  };
  
  if (events.length === 0) return null;
  
  return (
    <div className={`fixed bottom-4 left-4 z-50 ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } border rounded-lg shadow-lg max-w-md`}>
      <div 
        className="p-2 flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="text-sm font-medium">Workflow Events ({events.length})</h3>
        <button className="text-xs">
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
      
      {expanded && (
        <div className={`p-2 max-h-60 overflow-y-auto ${
          darkMode ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
          {events.map(event => (
            <div key={event.id} className="mb-2 text-xs">
              <div className="flex items-center">
                <span className={`font-medium ${getEventTypeStyle(event.type)}`}>
                  {event.type.toUpperCase()}
                </span>
                <span className="mx-1">•</span>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                  {event.timestamp.toLocaleTimeString()}
                </span>
              </div>
              <div className="mt-1">{event.message}</div>
              {event.details && (
                <pre className={`mt-1 p-1 rounded text-xs overflow-x-auto ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  {typeof event.details === 'object' 
                    ? JSON.stringify(event.details, null, 2)
                    : String(event.details)
                  }
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to log workflow events
export function logWorkflowEvent(type: 'info' | 'warning' | 'error' | 'success', message: string, details?: any) {
  const event = new CustomEvent('workflow-event', {
    detail: { type, message, details }
  });
  
  window.dispatchEvent(event);
  
  // Also log to console
  switch (type) {
    case 'error':
      console.error(`[Workflow] ${message}`, details);
      break;
    case 'warning':
      console.warn(`[Workflow] ${message}`, details);
      break;
    case 'success':
      console.log(`[Workflow] ${message}`, details);
      break;
    case 'info':
    default:
      console.info(`[Workflow] ${message}`, details);
      break;
  }
}
